import { apiClient } from "@/lib/api";
import type { AddressPartsValue } from "@/lib/addressParts";
import { newClientKey } from "@/lib/clientKey";
import type {
  AddressResolveResult,
  AddressSuggestion,
  AddressValidationResult,
} from "@/types";

interface AddressSuggestionsResponse {
  items: AddressSuggestion[];
}

function createSessionToken(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return newClientKey();
}

export const geocodingService = {
  createSessionToken,

  async suggest(
    query: string,
    sessionToken: string,
    limit = 5,
  ): Promise<AddressSuggestion[]> {
    const q = query.trim();
    if (q.length < 3) {
      return [];
    }
    const response = await apiClient.get<AddressSuggestionsResponse>(
      "/geocoding/suggestions",
      { params: { q, limit, session_token: sessionToken } },
    );
    return response.data.items ?? [];
  },

  async retrieve(mapboxId: string, sessionToken: string): Promise<AddressSuggestion> {
    const response = await apiClient.get<AddressSuggestion>(
      `/geocoding/retrieve/${encodeURIComponent(mapboxId)}`,
      { params: { session_token: sessionToken } },
    );
    return response.data;
  },

  async validateAddress(parts: AddressPartsValue): Promise<AddressValidationResult> {
    const response = await apiClient.post<{
      valid: boolean;
      reason?: string | null;
    }>("/geocoding/validate", {
      address: parts.address,
      city: parts.city,
      postal_code: parts.postal_code,
      country: parts.country,
    });
    if (response.data.valid) {
      return { valid: true };
    }
    return {
      valid: false,
      reason: response.data.reason ?? "unknown",
    };
  },

  async resolveAddress(address: string): Promise<AddressResolveResult> {
    const response = await apiClient.post<{
      coordinates?: { lat: number; lng: number } | null;
      reason?: string | null;
    }>("/geocoding/resolve", { address });
    if (response.data.coordinates) {
      return { coordinates: response.data.coordinates };
    }
    return {
      coordinates: null,
      reason: response.data.reason ?? "unknown",
    };
  },
};
